import debounce from "lodash.debounce";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ParticipantDashboardDataSelector } from "../../../redux/selectors/participantDashboardSelector";
import { IctDetailsErrorSelector } from "../../../redux/selectors/ictDetailsSelector";
import {
  setIctInventoryData,
  setIctInventoryPartialAnswersData,
} from "../../../redux/slice/ictDetailsSlice";
import { EMAIL_REGEX1, EMAIL_REGEX2 } from "../../../utils/common.regex";
import errorIcon from "../../../assets/styles/images/error-icon.png";
import { getNameKanjiValidationError } from "../../../utils/common.fucntion";

const options = [
  { point: 1, name: "全くあてはまらない" },
  { point: 2, name: "あてはまらない" },
  { point: 3, name: "ややあてはまらない" },
  { point: 4, name: "どちらともいえない" },
  { point: 5, name: "ややあてはまる" },
  { point: 6, name: "あてはまる" },
  { point: 7, name: "とてもよくあてはまる" },
];

const INTRO_PAGE = 1;
const MAX_CHAR = 300;
const REQUIRED_ERROR_MSG_1 = "* 選択は必須です";
const REQUIRED_ERROR_MSG_2 = "このフィールドは必須です。";
const INVALID_EMAIL_MSG = "不正なメールアドレスです。";
const identityMismatchErrorCodes = ["9044", "9045"];

const parseIdentityApiErrors = (error, t) => {
  const fieldErrors = {};
  let hasIdentityMismatch = false;

  if (!error?.details) {
    return { fieldErrors, hasIdentityMismatch };
  }

  error.details.forEach((detail) => {
    const fieldError = detail?.errors?.[0];
    const errorCode = fieldError?.error_code;

    if (!detail?.field || errorCode == null) {
      return;
    }

    fieldErrors[detail.field] = t(String(errorCode));

    if (identityMismatchErrorCodes.includes(String(errorCode))) {
      hasIdentityMismatch = true;
    }
  });

  return { fieldErrors, hasIdentityMismatch };
};

export const focusError = (firstErrorField) => {
  if (firstErrorField) {
    const targetDivs = document.querySelectorAll(".inv_highlight_head");
    let divFound = false;
    targetDivs.forEach((div) => {
      if (!divFound && div.getAttribute("id") === String(firstErrorField)) {
        div.scrollIntoView({ behavior: "smooth", block: "center" });
        divFound = true;
      }
    });
  }
};
//Function to validate the name and email fields
const getPostInventoryNameEmailErrors = (name, email) => {
  const errors = {};
  const trimmedEmail = email.trim();

  const nameError = getNameKanjiValidationError(name);
  if (nameError) {
    errors.name = nameError;
  }
  if (!trimmedEmail) {
    errors.email = REQUIRED_ERROR_MSG_2;
  } else if (
    !EMAIL_REGEX1.test(trimmedEmail) ||
    !EMAIL_REGEX2.test(trimmedEmail)
  ) {
    errors.email = INVALID_EMAIL_MSG;
  }

  return errors;
};

const getPostInventoryQuestionErrors = (questions, answers) => {
  const errors = {};
  questions?.forEach((item) => {
    if (
      item.required &&
      !answers.some((answer) => answer.question_id === item.id)
    ) {
      errors[String(item.id)] =
        item.type === 1 ? REQUIRED_ERROR_MSG_1 : REQUIRED_ERROR_MSG_2;
    }
  });
  return errors;
};

function buildQuestionPages(questions, pageLimits) {
  const limits = pageLimits?.slice(1) || [];

  const pages = [];
  let start = 0;

  limits.forEach((limit) => {
    if (limit > 0) {
      pages.push(questions.slice(start, start + limit));
      start += limit;
    }
  });

  return pages;
}
const normalizeQuestionText = (text) => {
  if (text == null) {
    return "";
  }

  return String(text).replace(/\\n/g, "\n");
};

const renderQuestionText = (question) => (
  <span style={{ whiteSpace: "pre-line" }}>{normalizeQuestionText(question)}</span>
);

function IctPostInventoryDetails(props) {
  const ictDetailsErrorData = useSelector(IctDetailsErrorSelector);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { inventory_id } = useParams();
  const {
    data,
    PartialAnswers,
    currentPage: savedCurrentPage,
    setPartialAnswer,
    setAnswer,
    setAnswerPage,
  } = props;
  const participantDashBoardData = useSelector(ParticipantDashboardDataSelector);
  const [prevUrlPage] = useState(participantDashBoardData?.page || 1);

  const questionsDetails = data?.questions;
  const pageLimits = data?.page_limits;
  const savedPage = savedCurrentPage > 0 ? savedCurrentPage : INTRO_PAGE;
  const questionsKey = data?.questions;
  const pageLimitsKey = data?.page_limits;

  const bodyRef = useRef({ answers: [] });
  const nameRef = useRef("");
  const emailRef = useRef("");
  const [qList, setQList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [questionsOnPage, setQuestionsOnPage] = useState([]);
  const [currentPage, setCurrentPage] = useState(savedPage);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [inputTexts, setInputTexts] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [buttonClicked, setButtonClicked] = useState(false);
  const [identityMismatchError, setIdentityMismatchError] = useState(false);
  const INITIAL_INVENTORY_REQUIRED_ERROR_CODE = "9040";
  const hasInitialInventoryRequiredError =
    String(ictDetailsErrorData?.data?.error?.error_code) ===
    INITIAL_INVENTORY_REQUIRED_ERROR_CODE;

  const debouncedSave = useMemo(
    () =>
      debounce((answerBody) => {
        setPartialAnswer?.(inventory_id, answerBody);
      }, 1000),
    [inventory_id, setPartialAnswer],
  );
  const resolveIdentity = (currentName, currentEmail) => ({
    name: String(
      currentName ?? nameRef.current ?? props?.PartialAnswers?.name ?? "",
    ).trim(),
    email: String(
      currentEmail ?? emailRef.current ?? props?.PartialAnswers?.email ?? "",
    ).trim(),
  });

  const getCurrentBody = (currentName = name, currentEmail = email) => ({
    email: String(currentEmail ?? "").trim(),
    name: String(currentName ?? "").trim(),
    answers: Array.isArray(bodyRef.current?.answers)
      ? bodyRef.current.answers
      : [],
  });

  const hasValidIdentity = (currentName, currentEmail) => {
    const { name: resolvedName, email: resolvedEmail } = resolveIdentity(
      currentName,
      currentEmail,
    );
    return (
      Object.keys(getPostInventoryNameEmailErrors(resolvedName, resolvedEmail)).length === 0
    );
  };

  const savePartialAnswer = (
    currentName,
    currentEmail,
    shouldDebounce = false,
  ) => {
    const currentBody = getCurrentBody(currentName, currentEmail);
    if (!hasValidIdentity(currentName, currentEmail)) {
      return;
    }

    if (shouldDebounce) {
      debouncedSave(currentBody);
      return;
    }

    props?.setPartialAnswer?.(inventory_id, currentBody);
  };

  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  useEffect(() => {
    const questionPages = buildQuestionPages(questionsDetails, pageLimits); // questions split into pages
    setQList(questionPages);
    setTotalPages(INTRO_PAGE + questionPages.length);

    const resumePage = Math.min(
      Math.max(savedPage, INTRO_PAGE),
      INTRO_PAGE + questionPages.length,
    );
    setCurrentPage(resumePage);
    setQuestionsOnPage(
      resumePage === INTRO_PAGE ? [] : questionPages[resumePage - 2] || [],
    );
  }, [questionsKey, pageLimitsKey, savedPage, questionsDetails, pageLimits]);

  useEffect(() => {
    return () => {
      dispatch(setIctInventoryData({}));
      dispatch(setIctInventoryPartialAnswersData({}));
    };
  }, [dispatch]);

  useEffect(() => {
    const partialAnswers = PartialAnswers;
    if (!partialAnswers) {
      return;
    }

    const savedAnswers = Array.isArray(partialAnswers?.answers)
      ? partialAnswers.answers
      : Array.isArray(partialAnswers)
        ? partialAnswers
        : [];

    const confirmInputData = {};
    const singleData = {};

    bodyRef.current = { answers: [...savedAnswers] };

    savedAnswers.forEach((answer) => {
      if (answer?.itemType === 2) {
        confirmInputData[answer.question_id] = answer.answer;
      } else if (answer?.itemType === 1) {
        const optionIndex = options.findIndex(
          (opt) => opt.point === answer.point,
        );
        if (optionIndex !== -1) {
          singleData[answer.question_id] = {
            index: optionIndex,
            point: answer.point,
          };
        }
      }
    });

    setInputTexts(confirmInputData);
    setSelectedOptions(singleData);
    const loadedName = partialAnswers?.name || "";
    const loadedEmail = partialAnswers?.email || "";
    nameRef.current = loadedName;
    emailRef.current = loadedEmail;
    setName(loadedName);
    setEmail(loadedEmail);
  }, [PartialAnswers]);

  const getPageForQuestionId = (questionId) => {
    for (let index = 0; index < qList.length; index += 1) {
      if (qList[index]?.some((question) => question.id === questionId)) {
        return index + 2;
      }
    }
    return null;
  };

  useEffect(() => {
    if (hasInitialInventoryRequiredError) {
      document.getElementById("initial-inventory-error")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [hasInitialInventoryRequiredError, currentPage]);

  const validatePostIntroPage = () => getPostInventoryNameEmailErrors(name, email);

  const validateCurrentQuestionPage = () =>
    getPostInventoryQuestionErrors(questionsOnPage, bodyRef.current.answers);

  const validateAllQuestions = () =>
    getPostInventoryQuestionErrors(questionsDetails, bodyRef.current.answers);

  const handleValidationFailure = (errors, fallbackPage = currentPage) => {
    setValidationErrors(errors);
    const firstErrorField = Object.keys(errors)[0];
    let targetPage = fallbackPage;

    if (firstErrorField === "name" || firstErrorField === "email") {
      targetPage = INTRO_PAGE;
    } else {
      targetPage = getPageForQuestionId(Number(firstErrorField)) || fallbackPage;
    }

    if (targetPage !== currentPage) {
      setCurrentPage(targetPage);
      setQuestionsOnPage(
        targetPage === INTRO_PAGE ? [] : qList[targetPage - 2] || [],
      );
    }

    focusError(firstErrorField);
  };

  const updateSavedPage = async (nextPage) => {
    if (currentPage >= (props?.currentPage || INTRO_PAGE)) {
      const saved = await setAnswerPage?.({ page: nextPage });
      return saved !== false;
    }
    return true;
  };
  const handleFinalCheck = () => {
    const errors = {
      ...getPostInventoryNameEmailErrors(name, email),
      ...validateAllQuestions(),
    };

    if (Object.keys(errors).length > 0) {
      setButtonClicked(true);
      handleValidationFailure(errors);
      return false;
    }

    return true;
  };

  const handlePostInventoryButtonClick = async () => {
    if (currentPage !== INTRO_PAGE) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setButtonClicked(true);

    let errors = {};
    if (currentPage === INTRO_PAGE) {
      errors = validatePostIntroPage();
    } else {
      errors = validateCurrentQuestionPage();
    }

    if (Object.keys(errors).length > 0) {
      handleValidationFailure(errors);
      return;
    }

    if (currentPage === INTRO_PAGE) {
      const saveResult = await props?.setPartialAnswer?.(
        inventory_id,
        getCurrentBody(),
      );

      if (saveResult?.success === false) {
        const { fieldErrors, hasIdentityMismatch } = parseIdentityApiErrors(
          saveResult.error,
          t,
        );

        if (Object.keys(fieldErrors).length > 0) {
          setValidationErrors(fieldErrors);
          focusError(Object.keys(fieldErrors)[0]);
        }

        if (hasIdentityMismatch) {
          setIdentityMismatchError(true);
        }

        return;
      }

      setIdentityMismatchError(false);
    }

    setValidationErrors({});

    if (currentPage === totalPages) {
      if (handleFinalCheck()) {
        debouncedSave.cancel();
        setAnswer?.(inventory_id, getCurrentBody());
      }
      return;
    }

    const nextPage = currentPage + 1;
    const pageSaved = await updateSavedPage(nextPage);
    if (!pageSaved) {
      return;
    }
    setCurrentPage(nextPage);
    setQuestionsOnPage(nextPage === INTRO_PAGE ? [] : qList[nextPage - 2] || []);
    setButtonClicked(false);
  };

  const handlePrevButtonClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const previousPage = currentPage - 1;
    setCurrentPage(previousPage);
    setQuestionsOnPage(
      previousPage === INTRO_PAGE ? [] : qList[previousPage - 2] || [],
    );
    setButtonClicked(false);
  };

  const handleChangeOption = (itemId, itemType, point, index2) => (e) => {
    if (itemType === 1) {
      setSelectedOptions((prevSelectedOptions) => ({
        ...prevSelectedOptions,
        [itemId]: {
          index: index2,
          point,
        },
      }));

      bodyRef.current.answers = bodyRef.current.answers.filter(
        (answer) => answer.question_id !== itemId,
      );
      bodyRef.current.answers.push({
        question_id: itemId,
        answer: "",
        point,
        itemType,
      });

      if (validationErrors[String(itemId)]) {
        setValidationErrors((prev) => {
          const next = { ...prev };
          delete next[String(itemId)];
          return next;
        });
      }

      savePartialAnswer(nameRef.current, emailRef.current);
    } else if (itemType === 2) {
      const inputText = e.target.value.slice(0, MAX_CHAR);
      e.target.value = inputText;

      if (inputText.trim().length === 0) {
        bodyRef.current.answers = bodyRef.current.answers.filter(
          (answer) => answer.question_id !== itemId,
        );
      } else {
        bodyRef.current.answers = bodyRef.current.answers.filter(
          (answer) => answer.question_id !== itemId,
        );
        bodyRef.current.answers.push({
          question_id: itemId,
          answer: inputText,
          point: 0,
          itemType,
        });
      }

      if (validationErrors[String(itemId)]) {
        setValidationErrors((prev) => {
          const next = { ...prev };
          delete next[String(itemId)];
          return next;
        });
      }

      savePartialAnswer(nameRef.current, emailRef.current, true);
    }
  };

  const renderType1Question = (item) => (
    <div className="width-100" key={item.id}>
      <div className="inv_highlight_head mt-5 pb-2 pl-3" id={String(item.id)}>
        {item?.question_no}. {renderQuestionText(item?.question)}
      </div>
      <div className="d-flex fl-dir pl-3 pr-3 options">
        {options.map((option, index2) => (
          <div
            className={`inv-border-box text-center flx_fullwidth ${index2 === 6 ? "inv-border-right" : ""} mb-2 inv_brd_right 
                  ${index2 === selectedOptions[item.id]?.index ? "inv_highlight" : ""}`}
            key={index2}
          >
            <div className="inv_rev invrevflt pb-2">
              <div className="text-center inv_text_ht pt-3 inv_quesize">
                {option.name}
              </div>
              <div className="text-center">
                <input
                  className="form-check-input position-inherit-radio mr-2 ml-2 invoice-p"
                  type="radio"
                  name={`flexRadioDefault_${item?.id}`}
                  id={`flexRadioDefault${item?.id}_${index2 + 1}`}
                  value={option.point}
                  checked={selectedOptions[item.id]?.index === index2}
                  onChange={handleChangeOption(
                    item?.id,
                    item?.type,
                    option.point,
                    index2,
                  )}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div
        className="text-end inv_text_limit options pr-3"
        style={{ marginTop: "-8px" }}
      >
        {buttonClicked && validationErrors[String(item.id)] && (
          <span className="text-danger pl-3 float-left">
            {validationErrors[String(item.id)]}
          </span>
        )}
      </div>
    </div>
  );

  const renderType2Question = (item) => (
    <div className="width-100" key={item.id}>
      <div className="inv_highlight_head mt-5 pb-2 pl-3" id={String(item.id)}>
        {item.question_no}. {renderQuestionText(item.question)}
      </div>
      <div className="options pl-3 pr-3 inv_details">
        <textarea
          className="form-control"
          id={`textarea_${item?.id}`}
          rows="6"
          value={inputTexts[item?.id] || ""}
          onChange={(e) => {
            const inputValue = e.target.value.slice(0, MAX_CHAR);
            setInputTexts((prevInputTexts) => ({
              ...prevInputTexts,
              [item.id]: inputValue,
            }));
            handleChangeOption(item?.id, item?.type, 0, 0)(e);
          }}
        ></textarea>
      </div>
      <div className="text-end inv_text_limit options pr-3">
        {buttonClicked && validationErrors[String(item.id)] && (
          <span className="text-danger pl-3 float-left">
            {validationErrors[String(item.id)]}
          </span>
        )}
        {inputTexts[item?.id]
          ? `${inputTexts[item?.id].length}/300字`
          : "0/300字"}
      </div>
    </div>
  );

  const renderIntroPage = () => (
    <>
      <div className="col-md-12 ">
        <div className="inv_highlight p-3 px-4">
          <div className="inv-survey-intro shade">
            <p className="inv-intro-section-title">【アンケートの目的】</p>
            <p>
              このアンケートは、ご自分のコミュニケーションを振り返ることを目的としています。
            </p>
            <p>1回目のインベントリーで使用したメールアドレスをご入力ください。。
            </p>
            {/* <p className="inv-intro-paragraph-gap">
            </p> */}
            <p>
            （異なるメールアドレスを登録すると、正しく結果を表示することができません。）
            </p>
            <p className="inv-intro-paragraph-gap">
              またプログラム実施報告の一環で貴社責任者に回答結果が共有されることがあります。
            </p>
            <p>
              その場合、選択式回答は複数名で集計され、記述回答は無記名で取り扱われます。
            </p>
            <p className="inv-intro-paragraph-gap">
              ご自身について率直な回答をお願いいたします。
            </p>

            <p className="inv-intro-section-title inv-intro-section-gap">
              【ご回答にあたってのお願い】
            </p>
            <p className="inv-intro-body-after-title">
              ご回答の所要時間は5分程度の想定です。
            </p>
            <p>
             プログラム終了までにご回答を終えていただきますよう、ご協力をお願いいたします。            </p>

            <p className="inv-intro-section-title inv-intro-section-gap">
              【アンケートに関する個人情報の取り扱いについて】
            </p>
            <p className="inv-intro-body-after-title">
              株式会社コーチ・エィは個人情報保護方針に従い、あなたの回答および個人情報を厳正に保管・管理いたします。
            </p>
          </div>
        </div>
      </div>

      <div className="width-100">
        <div className="inv_highlight_head mt-5 pb-2 pl-3" id="name">
          氏名
        </div>
        <div className="options pl-3 pr-3 inv_details">
          <input
            type="text"
            className="form-control"
            placeholder="山田 太郎"
            value={name}
            onChange={(e) => {
              const nameValue = e.target.value;
              nameRef.current = nameValue;
              setName(nameValue);
              if (validationErrors.name) {
                setValidationErrors((prev) => ({ ...prev, name: null }));
              }
              if (identityMismatchError) {
                setIdentityMismatchError(false);
              }
            }}
          />
        </div>
        {buttonClicked && validationErrors.name && (
          <div className="text-end inv_text_limit options pr-3">
            <span className="text-danger pl-3 float-left">
              {validationErrors.name}
            </span>
          </div>
        )}
      </div>

      <div className="width-100">
        <div className="inv_highlight_head mt-5 pb-2 pl-3" id="email">
          メールアドレス
        </div>
        <div className="options pl-3 pr-3 inv_details">
          <input
            type="email"
            className="form-control"
            placeholder="example@example.com.jp"
            value={email}
            onChange={(e) => {
              const nextEmail = e.target.value;
              emailRef.current = nextEmail;
              setEmail(nextEmail);
              if (validationErrors.email) {
                setValidationErrors((prev) => ({ ...prev, email: null }));
              }
              if (identityMismatchError) {
                setIdentityMismatchError(false);
              }
            }}
          />
        </div>
        {buttonClicked && validationErrors.email && (
          <div className="text-end inv_text_limit options pr-3">
            <span className="text-danger pl-3 float-left">
              {validationErrors.email}
            </span>
          </div>
        )}
      </div>
      <div className="ml-3 mt-3">
        <p className="text-secondary mb-0" style={{ fontSize: "13px" }}>
          ※このアンケートは、ワークショップの前に必ずご回答いただくものです。
        </p>
        <p className="text-secondary" style={{ fontSize: "13px" }}>
          アンケートへの回答有無を確認することがあるため、氏名・メールアドレスは正しくご入力ください。
        </p>
      </div>
    </>
  );

  const renderQuestionPageHeader = () => {
    if (currentPage !== 2) {
      return null;
    }

    return (
      <div className="col-md-12 ">
        <div className="inv_highlight p-2">
          <div className="fnt18 fw-bold mb-2">         
          今日一日を踏まえて、職場でのあなた自身を振り返ってお答えください。
          </div>
          <p className="ml-2">（選択式の設問が24項目あります）
          </p>
          <ul className="shade" style={{ marginLeft: "-13px" }}>
            <li>
              設問中の「相手」とは、あなたの部下や後輩を想定してください。
            </li>
            <li>
              部下がいない方は、日ごろ一緒に仕事をしている人やチームのメンバーを想定してください。
            </li>
          </ul>
          <div className="ml-2"> 
            選択肢の番号は以下を意味します。
            <div className="mt-3">1.全くあてはまらない、2.あてはまらない、3.ややあてはまらない、4.どちらともいえない、5.ややあてはまる、6.あてはまる、7.とてもよくあてはまる</div>
          </div>
        </div>
      </div>
    );
  };

  const renderNavigationButtons = () => {
    if (currentPage === totalPages) {
      return (
        <div className="mt-5 mb-5 options pl-3 pr-3">
          <div className="row">
            <div className="col-md-6 mb-2">
              <button
                type="button"
                className="btn btn-primary inv_full_btn w-100"
                onClick={handlePrevButtonClick}
              >
                前へ
              </button>
            </div>
            <div className="col-md-6 mb-2">
              <button
                type="button"
                className="btn btn-primary inv_full_btn w-100"
                onClick={handlePostInventoryButtonClick}
              >
                回答する
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (currentPage === INTRO_PAGE) {
      return (
        <div className="mt-4 mb-5 options pl-3 pr-3">
          <button
            type="button"
            className="btn btn-primary post_inv_full_btn w-100"
            onClick={handlePostInventoryButtonClick}
            disabled={identityMismatchError || hasInitialInventoryRequiredError}
          >
            次へ
          </button>
        </div>
      );
    }

    return (
      <div className="mt-5 mb-5 options pl-3 pr-3">
        <div className="row">
          <div className="col-md-6 mb-2">
            <button
              type="button"
              className="btn btn-primary inv_full_btn w-100"
              onClick={handlePrevButtonClick}
            >
              前へ
            </button>
          </div>
          <div className="col-md-6">
            <button
              type="button"
              className="btn btn-primary inv_full_btn w-100"
              onClick={handlePostInventoryButtonClick}
            >
              次へ
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!questionsDetails || questionsDetails.length === 0) {
    return (
      <div className="container w40 max-width-900">
        <div className="row my-5">
          <div className="h6 mx-auto mt-3">データなし</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container w40 max-width-900">
      {hasInitialInventoryRequiredError && (
        <div
          id="initial-inventory-error"
          className="mt-4 mb-3 text-center error-alert"
          role="alert"
        >
          <img className="icon-alert" src={errorIcon} alt="" />
          {" " + t(INITIAL_INVENTORY_REQUIRED_ERROR_CODE)}
        </div>
      )}
      <div className="row my-5">
        {totalPages > 1 && (
          <div className="row justify-content-center p-2 m-0 w-100">
            <div className="col-md-6 text-center my-5 align-self-center">
              <div className="progress grad">
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${(currentPage / totalPages) * 100}%` }}
                  aria-valuenow={currentPage}
                  aria-valuemin="0"
                  aria-valuemax={totalPages}
                ></div>
              </div>
            </div>
            <div className="col-md-2 align-self-center font-weight-bold text-center">
              {currentPage}/{totalPages}ページ
            </div>
          </div>
        )}

        {currentPage === INTRO_PAGE && renderIntroPage()}

        {currentPage !== INTRO_PAGE && (
          <>
            {renderQuestionPageHeader()}
            {questionsOnPage?.map((item) =>
              item.type === 1
                ? renderType1Question(item)
                : renderType2Question(item),
            )}
          </>
        )}

        {renderNavigationButtons()}

        {totalPages > 1 && (
          <div className="row justify-content-center p-2 m-0 w-100">
            <div className="col-md-6 text-center my-5 align-self-center">
              <div className="progress grad">
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${(currentPage / totalPages) * 100}%` }}
                  aria-valuenow={currentPage}
                  aria-valuemin="0"
                  aria-valuemax={totalPages}
                ></div>
              </div>
            </div>
            <div className="col-md-2 align-self-center font-weight-bold text-center">
              {currentPage}/{totalPages}ページ
            </div>
          </div>
        )}

        <div className="text-center mt-3 mx-auto">
          <Link
            to={`/participants/ict/post-inventories?&page=${prevUrlPage}`}
            className="top_links"
          >
            インベントリー2回目一覧に戻る
          </Link>
        </div>
      </div>
    </div>
  );
}

export default IctPostInventoryDetails;
